import { useCallback } from 'react';

import log from 'loglevel';

import {
  InstagramConversation,
  useGetInstagramConversations,
} from '../../../services/meta.service';
import {
  useInstagramCommunicationsStore,
  useIntegrationsStore,
} from '../../../store';
import { useActiveTeamId } from '../../../store/Team/useTeamStore';
import { useGetInstagramIntegrations } from '../../Integrations';

export const useFetchInstagramConversations = () => {
  const { setLoading, saveInstagramConversations } =
    useInstagramCommunicationsStore();
  const teamInstagramIntegrations = useIntegrationsStore(
    (state) => state.teamInstagramIntegrations,
  );
  const teamId = useActiveTeamId();
  const { handleFetchInstagramIntegrations } = useGetInstagramIntegrations();
  const { getInstagramConversations } = useGetInstagramConversations();

  const handleFetchInstagramConversations = useCallback(async () => {
    if (!teamId) {
      log.warn('No active team. Cannot fetch Instagram conversations.');
      return;
    }

    setLoading(true);
    try {
      // The integrations guard may still be cold on a hard refresh into /inbox/instagram
      if (!teamInstagramIntegrations?.length) {
        await handleFetchInstagramIntegrations(teamId);
      }

      const integrations =
        useIntegrationsStore.getState().teamInstagramIntegrations;

      if (!integrations?.length) {
        log.warn('No Instagram integration found. Cannot fetch conversations.');
        saveInstagramConversations([]);
        return;
      }

      const conversationPromises = integrations.map(
        async ({ platformAccountId }) => {
          try {
            return await getInstagramConversations(teamId, platformAccountId);
          } catch (error) {
            log.error(
              `Error fetching Instagram conversations for account ${platformAccountId}:`,
              error,
            );
            return [] as InstagramConversation[];
          }
        },
      );

      const allConversations = (await Promise.all(conversationPromises)).flat();

      saveInstagramConversations(allConversations);
    } catch (error) {
      log.error('Error fetching Instagram conversations:', error);
    } finally {
      setLoading(false);
    }
  }, [teamId, teamInstagramIntegrations?.length]);

  return { handleFetchInstagramConversations };
};
